import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { isCapacitor, isElectron } from '../utils/platform';
import { authManager } from '../services/AuthManager';
import { profileManager, userService } from '../services';
import { createLogger } from '../services/Debug';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const log = createLogger('AuthProvider');
  const [authReady, setAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // Phone-based user info

  const computeIsSubscribed = useCallback((p) => {
    if (!p) return false;
    if (typeof p.isSubscriptionActive === 'boolean') return p.isSubscriptionActive;
    return p.subscription?.status === 'active';
  }, []);

  const refreshProfile = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    try {
      const profileData = await userService.getProfile();
      const loaded = profileManager.loadFromAPI(profileData);
      setProfile(loaded || profileData || null);

      // Update current user from auth manager
      const user = authManager.getCurrentUser();
      if (user) {
        setCurrentUser(user);
      }

      return loaded || profileData || null;
    } catch (e) {
      log.warn(`[Auth] refreshProfile failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, e?.message || e);

      // Retry logic with exponential backoff
      if (retryCount < MAX_RETRIES) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
        log.info(`[Auth] Retrying profile fetch in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return refreshProfile(retryCount + 1);
      }

      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const ok = await authManager.initialize();
        if (!mounted) return;
        setIsAuthenticated(ok);

        let p = profileManager.getCurrentProfile();
        if (!p && ok) {
          p = await refreshProfile();
        }
        if (mounted) {
          setProfile(p || null);
        }
      } catch (e) {
        if (mounted) {
          setIsAuthenticated(false);
          setProfile(null);
        }
      } finally {
        if (mounted) {
          setAuthReady(true);
          setLoading(false);
        }
      }
    })();

    const unsubscribe = authManager.addAuthStateListener(async (authed) => {
      setIsAuthenticated(authed);
      if (authed) {
        await refreshProfile();
      } else {
        setProfile(null);
      }
    });

    return () => { mounted = false; unsubscribe && unsubscribe(); };
  }, [refreshProfile]);

  // Enforce 24h auto-logout on web when "Rester connecté" is unchecked
  useEffect(() => {
    if (isCapacitor || isElectron) return; // web-only behavior

    let timerId = null;
    try {
      const remember = localStorage.getItem('rememberMe');
      const expiryAtStr = localStorage.getItem('sessionExpiryAt');
      log.debug('[RememberMe] State', { remember, expiryAtStr, isAuthenticated });

      if (remember === '0') {
        const expiryAt = parseInt(expiryAtStr || '0', 10);
        const now = Date.now();

        if (isAuthenticated) {
          if (!expiryAt || expiryAt <= now) {
            // Expired or missing expiry while in unremembered mode: logout immediately
            log.info('[RememberMe] Expired or missing expiry → logout now');
            authManager.logout();
          } else {
            // Schedule logout at expiry time
            const delay = Math.max(0, expiryAt - now);
            log.info('[RememberMe] Scheduling auto-logout', { delayMs: delay, expiryAt });
            timerId = window.setTimeout(() => {
              log.info('[RememberMe] Auto-logout firing');
              authManager.logout();
            }, delay);
          }
        } else {
          // Not authenticated: if expired, ensure cleanup of stale flags
          if (expiryAt && expiryAt <= now) {
            log.debug('[RememberMe] Cleaning stale expiry flag while logged out');
            localStorage.removeItem('sessionExpiryAt');
          }
        }
      }
    } catch (_) {}

    return () => {
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [isAuthenticated]);

  const isSubscribed = useMemo(() => computeIsSubscribed(profile), [profile, computeIsSubscribed]);
  // Onboarding removed

  // Compute phone-based user state
  const hasPin = useMemo(() => Boolean(currentUser?.has_pin), [currentUser]);
  const phoneNumber = useMemo(() => currentUser?.phone_e164 || null, [currentUser]);
  const emailVerified = useMemo(() => Boolean(currentUser?.email_verified), [currentUser]);

  const value = useMemo(() => ({
    authReady,
    loading,
    isAuthenticated,
    profile,
    isSubscribed,
    refreshProfile,
    // New phone-based auth properties
    currentUser,
    hasPin,
    phoneNumber,
    emailVerified
  }), [authReady, loading, isAuthenticated, profile, isSubscribed, refreshProfile, currentUser, hasPin, phoneNumber, emailVerified]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}


